"use client"

import { useState, useEffect, useRef } from "react"
import HomeScreen from "./components/home-screen"
import ProcessingScreen from "./components/processing-screen"
import TextDisplayScreen from "./components/text-display-screen"
import ErrorScreen from "./components/error-screen"
import CameraScreen from "./components/functions/camera/camera-screen"
import { normalizeOCRText, performOCR, performGeminiOCRForPdf, isOcrCancellation, isOcrTimeout, isOcrOffline } from './components/functions/util/ocr-client';
import { NavigationStack } from './components/functions/util/navigation-stack';
import { logger } from '@/lib/logger';
import { clearJobContext, getJobContext, setJobContext } from '@/lib/job-context';
import { throwIfCrashSelftestRequested, throwIfCrashAfterJobRequested } from '@/lib/selftest';
import type { NormalizeResult, OcrPageResult, OcrSummary, PerformOcrResult } from './components/functions/util/ocr-types';

type Screen = "home" | "processing" | "display" | "camera" | "cameraPicture" | "openFilePicture" | "pdfView" | "error"
import PictureScreen from "./components/functions/picture/picture-screen";
import PdfViewScreen from "./components/functions/pdf/pdf-view";
import { ProcessingScreenProgressManager, ProcessingProgressStateData as ProcessingProgressState } from './components/functions/util/processing-screen-progress-manager'
import { PAGE_BREAK_TOKEN } from './components/functions/util/constants'
import { detectDocTypeFromText, pickDocumentType } from './components/functions/util/form-detector'
import { cleanupOcrText } from './components/functions/util/ocr-cleanup'
import { mergeSoftWrappedBookLines } from './components/functions/util/book-line-merge'
import { importDocxToText } from './components/functions/util/docx-import'


export default function DyslexaApp() {
    /*
     * Before any hooks, so the render ends here and app/error.tsx takes over.
     * Only fires for ?selftest=crash — see lib/selftest.ts for why the trigger
     * has to live in the code at all, and docs/HANDOVER.md for the procedure,
     * which matters: crashing on a cold load proves nothing about redaction.
     */
    throwIfCrashSelftestRequested();
    // Second mode: fires on the render where finished text would appear, so the
    // report carries a real job and a real file name to redact. See lib/selftest.ts.
    throwIfCrashAfterJobRequested(getJobContext().stage);

    type ResolvedDocType = 'book' | 'form' | 'generic';
    type PreparedOcrPageResult = OcrPageResult & {
        effectiveDocType: ResolvedDocType;
    };
    const [navManager] = useState(() => new NavigationStack());
    const [screenStack, setScreenStack] = useState(navManager.getStack());
    const [progressManager] = useState(() => new ProcessingScreenProgressManager());
    const [progressState, setProgressState] = useState<ProcessingProgressState>(progressManager.getState());
    const isProcessing = useRef<boolean>(false);
    /*
     * Handle on the network work of the current job. isProcessing alone is not
     * enough: the loop guards that read it only run once an await has returned, so
     * for the 35 s an OCR request takes, "Anuluj" left the request in flight and
     * the user waiting on something they believed they had stopped.
     */
    const jobAbortRef = useRef<AbortController | null>(null);

    /** Starts a job, superseding any previous one. */
    const beginJob = (): void => {
        jobAbortRef.current?.abort();
        jobAbortRef.current = new AbortController();
    };

    const currentSignal = (): AbortSignal | undefined => jobAbortRef.current?.signal;

    /*
     * Shown when a request hit its own ceiling rather than failing outright. It
     * names the likely cause the user can act on - leaving the screen - because
     * backgrounding the app is what kills the request on iOS.
     */
    const TIMEOUT_MESSAGE = "Przetwarzanie trwało zbyt długo i zostało przerwane. Sprawdź połączenie i spróbuj ponownie, nie zamykając tego ekranu.";

    /*
     * Says how far the document got, because the pages already recognised are
     * kept and a retry picks up from there. Both variants include the word
     * "połączenie" in lower case, which is what routes the error screen to its
     * connection troubleshooting tips.
     */
    const offlineMessage = (completedPages: number, totalPages: number): string =>
        completedPages > 0
            ? `Połączenie z internetem zostało przerwane po ${completedPages} z ${totalPages} stron. Rozpoznane strony zostały zapisane — sprawdź połączenie i spróbuj ponownie, aby dokończyć dokument.`
            : "Brak połączenia z internetem, więc nie udało się rozpocząć rozpoznawania. Sprawdź połączenie i spróbuj ponownie.";
    
    useEffect(() => {
        logger.info('App initialized', { sessionId: logger.getSessionId() });
        
        // Set up change listeners first
        navManager.onChange(setScreenStack);
        progressManager.onChange(setProgressState);

        // Restore navigation state first
        navManager.restoreFromStorage();

        let cancelled = false;
        
        // Handle async restoration and processing logic
        (async () => {
            try {
                // Always cancel any in-memory processing state on refresh
                // This ensures clean state after browser refresh
                if (isProcessing.current) {
                    await cancelProcessing();
                }
                
                // Restore progress state from storage
                await progressManager.restoreFromStorage();
                if (cancelled) return;
                
                const currentScreen = navManager.getCurrentScreen();
                logger.debug('Screen restored', { screen: currentScreen });
                
                // Check if we were in the middle of processing before refresh
                if (progressManager.isActive()) {
                    logger.info('Resuming active processing');
                    
                    // If we're not on processing screen but processing is active, navigate to it
                    if (currentScreen !== 'processing') {
                        navManager.jumpTo('processing');
                    }
                    
                    // Auto-resume processing with restored files
                    const state = progressManager.getState();
                    if (state.files && state.files.length > 0) {
                        logger.info('Resuming with files', { fileCount: state.files.length });
                        // Small delay to ensure navigation is complete
                        setTimeout(() => {
                            if (!cancelled) {
                                handlePdfOcrToText(state.files);
                            }
                        }, 500); // Increased delay for better reliability
                    } else {
                        logger.warn('No files found to resume');
                        // If no files, clear the processing state
                        await progressManager.reset();
                    }
                } else {
                    logger.debug('No active processing to resume');
                }
            } catch (error) {
                const message = error instanceof Error ? error.message : 'Unknown initialization error';
                logger.error('App initialization failed, applying recovery fallback', { error: message });
                isProcessing.current = false;
                await progressManager.reset().catch(() => undefined);
                navManager.clearStorage();
                navManager.reset();
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [navManager, progressManager]);

    // Image collection for multi-image scanning
    const [imageCollection, setImageCollection] = useState<File[]>([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    
    // Legacy single file reference (computed from collection for backwards compatibility)
    const cameraImageSrc = imageCollection.length > 0 ? imageCollection[currentImageIndex] : null;
    
    const [processedText, setProcessedText] = useState("")
    const [errorMessage, setErrorMessage] = useState("")
    const [ocrProvider, setOcrProvider] = useState<'gemini' | 'tesseract' | 'docx' | null>(null)
    const [ocrSummary, setOcrSummary] = useState<OcrSummary | null>(null)
    const OCR_RETRY_LADDER_ENABLED = process.env.NEXT_PUBLIC_OCR_RETRY_LADDER_ENABLED !== 'false'
    const OCR_NORMALIZE_ENABLED = process.env.NEXT_PUBLIC_OCR_NORMALIZE_ENABLED !== 'false'
    const PDF_SEND_DIRECT_TO_GEMINI = process.env.NEXT_PUBLIC_PDF_SEND_DIRECT_TO_GEMINI !== 'false'

    const createDocId = () => {
        if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
            return crypto.randomUUID();
        }
        return `doc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    };

    const createMissingPageMarker = (pageNumber: number): string => {
        return `[BRAK TEKSTU - STRONA ${pageNumber}]`;
    };

    const countTokenOccurrences = (text: string, token: string): number => {
        if (!token) {
            return 0;
        }

        return text.split(token).length - 1;
    };

    const applyNormalizationResult = (
        rawText: string,
        normalizeResult: NormalizeResult,
        docTypeHint: ResolvedDocType,
        pageIndex: number,
        contextLabel: string
    ): {
        finalText: string;
        normalized: boolean;
        usedRawFallback: boolean;
    } => {
        let finalText = normalizeResult.normalizedText;
        let normalized = !normalizeResult.usedRawFallback;
        const usedRawFallback = normalizeResult.usedRawFallback;

        if (usedRawFallback && docTypeHint === 'book') {
            const mergedFallbackText = mergeSoftWrappedBookLines(finalText);
            if (mergedFallbackText !== finalText) {
                logger.info(`Applied client-side fallback line merge for ${contextLabel} book text`, {
                    pageIndex,
                    beforeLength: finalText.length,
                    afterLength: mergedFallbackText.length,
                });
                finalText = mergedFallbackText;
            }
        }

        if (finalText === rawText) {
            normalized = false;
        }

        return {
            finalText,
            normalized,
            usedRawFallback,
        };
    };

    const buildOcrSummary = (pageResults: OcrPageResult[]): OcrSummary => {
        const fallbackPageIndices = pageResults
            .filter((page) => page.status === 'fallback_tesseract_ok')
            .map((page) => page.pageIndex);
        const missingPageIndices = pageResults
            .filter((page) => page.status === 'gemini_empty' || page.status === 'gemini_blocked' || page.status === 'failed')
            .map((page) => page.pageIndex);

        return {
            totalPages: pageResults.length,
            geminiPages: pageResults.filter((page) => page.status === 'gemini_ok').length,
            tesseractPages: fallbackPageIndices.length,
            missingPages: missingPageIndices.length,
            fallbackReason: fallbackPageIndices.length > 0 ? 'Gemini retries exhausted' : undefined,
            fallbackPageIndices,
            missingPageIndices,
        };
    };

    // For processing screen
    const [currentStep, setCurrentStep] = useState<number | undefined>(undefined)
    const [totalSteps, setTotalSteps] = useState<number | undefined>(undefined)

    // Image collection helper functions
    const addImageToCollection = (file: File) => {
        setImageCollection(prev => [...prev, file]);
        setCurrentImageIndex(imageCollection.length); // Point to the newly added image
    };

    const updateCurrentImage = (file: File) => {
        setImageCollection(prev => {
            const updated = [...prev];
            updated[currentImageIndex] = file;
            return updated;
        });
    };

    const removeImageFromCollection = (index: number) => {
        setImageCollection(prev => {
            const newCollection = prev.filter((_, i) => i !== index);
            
            // If no images left, go back to home
            if (newCollection.length === 0) {
                navManager.goBack();
                return [];
            }
            
            // Adjust current index if needed
            if (index <= currentImageIndex) {
                // If deleted image was before or at current, move index back
                setCurrentImageIndex(Math.max(0, currentImageIndex - 1));
            } else if (currentImageIndex >= newCollection.length) {
                // If current index is now out of bounds
                setCurrentImageIndex(newCollection.length - 1);
            }
            
            return newCollection;
        });
    };

    const clearImageCollection = () => {
        setImageCollection([]);
        setCurrentImageIndex(0);
    };

    const navigateToImageInCollection = (index: number) => {
        if (index >= 0 && index < imageCollection.length) {
            setCurrentImageIndex(index);
        }
    };

    // Use screenStack state to get current screen, ensuring it updates when navigation changes
    // Always ensure we have a valid screen, defaulting to 'home' if invalid or missing required state
    const getCurrentScreen = (): Screen => {
        if (screenStack.length === 0) return 'home';
        const screen = screenStack[screenStack.length - 1];
        const validScreens: Screen[] = ["home", "processing", "display", "camera", "cameraPicture", "openFilePicture", "pdfView", "error"];
        
        if (!validScreens.includes(screen as Screen)) {
            return 'home';
        }
        
        const currentScreen = screen as Screen;
        // Screens that require cameraImageSrc state
        const screensRequiringFile = ["cameraPicture", "openFilePicture", "pdfView"];
        if (screensRequiringFile.includes(currentScreen) && !cameraImageSrc) {
            return 'home';
        }
        
        return currentScreen;
    };
    const currentScreen = getCurrentScreen();

    const handleError = (message: string) => {
        logger.error('Error occurred', { message });
        setErrorMessage(message)
        navManager.navigateTo('error')
    }

    const handleBackToHome = async () => {
        clearJobContext()
        navManager.goBack()
        setProcessedText("")
        setErrorMessage("")
        setOcrProvider(null)
        setOcrSummary(null)
        clearImageCollection()
        await progressManager.reset()
    }

    const handleNavigateToHome = async () => {
        // If processing is active, cancel it first
        if (progressManager.isActive() || isProcessing.current) {
            await cancelProcessing();
        }
        
        // Clear storage and explicitly navigate to home
        navManager.clearStorage()
        // Reset stack to just home, then navigate to ensure we're on home
        navManager.reset()
        // Force navigate to home in case reset kept a different screen
        if (navManager.getCurrentScreen() !== 'home') {
            navManager.navigateTo('home');
        }
        
        setProcessedText("")
        setErrorMessage("")
        setOcrProvider(null)
        setOcrSummary(null)
        clearImageCollection()
        await progressManager.reset()
    }

    const handleCamera = () => {
        navManager.reset()
        clearImageCollection(); // Clear any previous collection
        setProcessedText(
            "Za chwilę powinien pojawić się obraz z kamery",
        )
        navManager.navigateTo("camera")
    }

    // Track camera capture mode: 
    // 'initial' - first capture (navigateTo cameraPicture)
    // 'retake' - replacing current image (goBack to cameraPicture)  
    // 'addMore' - adding to collection (goBack to cameraPicture)
    const [cameraCaptureMode, setCameraCaptureMode] = useState<'initial' | 'retake' | 'addMore'>('initial');

    const handleCameraPicture = (file: File) => {
        if (cameraCaptureMode === 'retake' && imageCollection.length > 0) {
            // Retake mode: replace the current image instead of adding
            updateCurrentImage(file);
        } else {
            // Initial or addMore mode: add to collection
            addImageToCollection(file);
        }
        
        // Navigate based on mode
        if (cameraCaptureMode === 'initial') {
            // First capture - push cameraPicture onto stack
            navManager.navigateTo('cameraPicture');
        } else {
            // Retake or addMore - go back to existing cameraPicture screen
            navManager.goBack();
        }
        
        // Reset to initial mode after capture
        setCameraCaptureMode('initial');
    }

    // Handler for adding more images to the collection
    const handleAddMoreToCollection = (file: File) => {
        addImageToCollection(file);
    }

    // Handler for when user wants to add more from camera (returns to camera screen)
    const handleAddMoreFromCamera = () => {
        setCameraCaptureMode('addMore');
        navManager.navigateTo('camera');
    }

    // Handler for retaking the current image from camera
    const handleRetakeFromCamera = () => {
        setCameraCaptureMode('retake');
        navManager.navigateTo('camera');
    }

    // Handler for processing all images in the collection
    const handleOCRImageCollection = async () => {
        if (imageCollection.length === 0) return;
        
        if (imageCollection.length === 1) {
            // Single image - use existing single image flow
            await handleFileUpload(imageCollection[0], 0, false);
        } else {
            // Multiple images - use multi-page OCR flow (like PDF)
            setJobContext({ source: 'image-collection' });
            await handlePdfOcrToText(imageCollection);
        }
    }

    const handleCameraPictureOCR = async (file: File, orientation: number) => {
        await handleFileUpload(file, orientation, false)
    }

    const handleOpenedPictureOCR = async (file: File, orientation: number) => {
        await handleFileUpload(file, orientation, false)
    }

    const handleFileUpload = async (file: File, rotation: number = 0, reset: boolean = true): Promise<void> => {
        logger.info('Single file OCR started', { fileName: file.name, fileSize: file.size, fileType: file.type });
        setOcrSummary(null);

        setJobContext({
            source: file.type.includes('pdf') ? 'pdf-direct' : file.type.includes('image') ? 'image' : 'docx',
            stage: 'ocr',
            fileCount: 1,
            totalBytes: file.size,
            fileType: file.type,
        });
        
        // Clear any previous processing state before starting new processing
        await progressManager.reset();
        navManager.jumpTo('processing')

        const isImage = file.type.includes("image")
        const isPdf = file.type.includes("pdf")
        const isDocx =
            file.name.toLowerCase().endsWith('.docx') ||
            file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

        // Process image file with proper progress tracking
        if (isImage) {
            try {
                beginJob();
                // Initialize with 1 step for single image processing
                await progressManager.initialize(1, [file]);
                
                // Brief delay to show the processing screen
                await sleep(200);
                
                // Perform OCR
                const docId = createDocId();
                const result = await ocrFile(file, rotation, false, { docId, pageIndex: 0, docTypeHint: 'generic' });

                const hasText = result.text.trim().length > 0;
                let finalText = result.text;
                let normalized = false;
                let usedRawFallback = false;

                if (hasText && OCR_NORMALIZE_ENABLED) {
                    const normalizeResult = await normalizeOCRText(result.text, {
                        pageIndex: 0,
                        docTypeHint: result.effectiveDocType,
                        signal: currentSignal(),
                    });

                    logger.info('OCR normalization completed', {
                        pageIndex: 0,
                        usedRawFallback: normalizeResult.usedRawFallback,
                        rejectReason: normalizeResult.rejectReason,
                        driftRatio: normalizeResult.stats.driftRatio,
                        inputLength: normalizeResult.stats.inputLength,
                        outputLength: normalizeResult.stats.outputLength,
                    });

                    const normalizedResult = applyNormalizationResult(
                        result.text,
                        normalizeResult,
                        result.effectiveDocType,
                        0,
                        'single-image'
                    );
                    finalText = normalizedResult.finalText;
                    normalized = normalizedResult.normalized;
                    usedRawFallback = normalizedResult.usedRawFallback;
                } else if (hasText) {
                    logger.debug('OCR normalization skipped by feature flag', { pageIndex: 0 });
                }

                const finalPageResult: OcrPageResult = {
                    ...result,
                    text: finalText,
                    normalized,
                };

                setProcessedText(finalText);
                setJobContext({ stage: 'display', provider: finalPageResult.provider });
                setOcrProvider(finalPageResult.provider === 'tesseract' ? 'tesseract' : 'gemini');
                setOcrSummary(buildOcrSummary([finalPageResult]));
                
                logger.info('Single file OCR completed', {
                    provider: finalPageResult.provider,
                    textLength: finalText.length,
                    status: finalPageResult.status,
                    normalized,
                    usedFallback: finalPageResult.usedFallback,
                    usedRawFallback,
                });
                
                // Mark as complete and navigate immediately
                // No delay - user sees smooth transition from ~95% to results
                await progressManager.setStep(1);
                
                if (reset) {
                    navManager.reset()
                }
                
                await progressManager.reset();
                navManager.jumpTo('display')
            } catch (err) {
                if (isOcrCancellation(err)) {
                    logger.info('Single file OCR aborted by cancellation');
                    return;
                }
                await progressManager.reset();
                const errorMsg = err instanceof Error ? err.message : "Nie udało się przetworzyć obrazu. Spróbuj ponownie.";
                logger.error('Single file OCR failed', { error: err instanceof Error ? err : errorMsg });
                setErrorMessage(errorMsg);
                navManager.navigateTo('error')
            }
        } else if (isPdf) {
            // Route PDFs to the dedicated PDF handler
            await handlePdfDirectToText(file)
        } else if (isDocx) {
            // DOCX import: extract raw text without OCR
            try {
                logger.info('DOCX import started', { fileName: file.name, fileSize: file.size, fileType: file.type })

                beginJob();
                // Reuse processing screen for consistency
                await progressManager.reset()
                navManager.jumpTo('processing')
                await progressManager.initialize(1, [file])
                await sleep(200)

                const text = await importDocxToText(file, logger)

                setProcessedText(text)
                setOcrProvider('docx')
                setOcrSummary(null)

                await progressManager.setStep(1, text)
                await new Promise(resolve => setTimeout(resolve, 300))

                if (reset) {
                    navManager.reset()
                }

                await progressManager.reset()
                navManager.jumpTo('display')
            } catch (err) {
                await progressManager.reset()
                const errorMsg = err instanceof Error ? err.message : "Nie udało się wczytać dokumentu Word (.docx). Spróbuj ponownie."
                logger.error('DOCX import failed', { error: err instanceof Error ? err : errorMsg })
                setErrorMessage(errorMsg)
                navManager.navigateTo('error')
            }
        } else {
            logger.warn('Unsupported file type', { fileType: file.type });
            setErrorMessage("Nieobsługiwany format pliku. Proszę przesłać obraz (JPG, PNG), plik PDF lub dokument Word (.docx).")
            navManager.navigateTo('error')
        }
    }

    const sleep = async (ms: number) => {
        return await new Promise(resolve => setTimeout(resolve, ms));
    }

    const handlePdfDirectToText = async (pdfFile: File) => {
        logger.info('PDF direct OCR started', { fileName: pdfFile.name, fileSize: pdfFile.size, fileType: pdfFile.type });
        setOcrSummary(null);

        setJobContext({
            source: 'pdf-direct',
            stage: 'ocr',
            fileCount: 1,
            totalBytes: pdfFile.size,
            fileType: pdfFile.type,
            pageIndex: 0,
        });

        // Clear any previous processing state before starting new processing
        await progressManager.reset();
        navManager.jumpTo('processing')

        try {
            beginJob();
            // For direct-to-Gemini flow we only need progress metadata.
            // Persisting the whole PDF inflates IndexedDB usage on mobile Safari.
            await progressManager.initialize(1, []);

            // Brief delay to show the processing screen
            await sleep(200);

            const docId = createDocId();
            const result: PerformOcrResult = await performGeminiOCRForPdf(pdfFile, {
                docId,
                pageIndex: 0,
                retryBudget: OCR_RETRY_LADDER_ENABLED ? 3 : 1,
                signal: currentSignal(),
            });

            const cleanedRawText = cleanupOcrText(result.text, { docTypeHint: 'book' });

            const hasText = cleanedRawText.trim().length > 0;
            if (!hasText) {
                throw new Error("recognition: Nie udało się rozpoznać tekstu w pliku PDF. Spróbuj ponownie.");
            }

            let finalText = cleanedRawText;
            let normalized = false;
            let usedRawFallback = false;

            if (OCR_NORMALIZE_ENABLED) {
                const normalizeResult = await normalizeOCRText(cleanedRawText, {
                    pageIndex: 0,
                    docTypeHint: 'book',
                    signal: currentSignal(),
                });
                finalText = normalizeResult.normalizedText;
                normalized = !normalizeResult.usedRawFallback;
                usedRawFallback = normalizeResult.usedRawFallback;

                logger.info('PDF normalization completed', {
                    pageIndex: 0,
                    usedRawFallback: normalizeResult.usedRawFallback,
                    rejectReason: normalizeResult.rejectReason,
                    driftRatio: normalizeResult.stats.driftRatio,
                    inputLength: normalizeResult.stats.inputLength,
                    outputLength: normalizeResult.stats.outputLength,
                });
            } else {
                logger.debug('PDF normalization skipped by feature flag', { pageIndex: 0 });
            }

            if (usedRawFallback) {
                const mergedFallbackText = mergeSoftWrappedBookLines(finalText);
                if (mergedFallbackText !== finalText) {
                    logger.info('Applied client-side fallback line merge for PDF book text', {
                        pageIndex: 0,
                        beforeLength: finalText.length,
                        afterLength: mergedFallbackText.length,
                    });
                    finalText = mergedFallbackText;
                }
            }

            setProcessedText(finalText);
            setOcrProvider('gemini');

            const pageResult: OcrPageResult = {
                pageIndex: 0,
                status: 'gemini_ok',
                text: finalText,
                rawText: result.rawText,
                normalized,
                provider: 'gemini',
                usedFallback: false,
                blocked: result.blocked,
                blockReason: result.blockReason,
                attempts: result.attempts,
            };

            const summary = buildOcrSummary([pageResult]);
            setOcrSummary(summary);

            await progressManager.setStep(1, finalText);

            // Wait for completion animation (500ms) + pause at 100% for user to see (300ms)
            await new Promise(resolve => setTimeout(resolve, 800));

            await progressManager.reset();
            navManager.jumpTo('display')
        } catch (err) {
            if (isOcrCancellation(err)) {
                logger.info('PDF direct OCR aborted by cancellation');
                return;
            }
            const errorMsg = isOcrOffline(err)
                ? offlineMessage(0, 1)
                : isOcrTimeout(err)
                ? TIMEOUT_MESSAGE
                : err instanceof Error ? err.message : "Nie udało się przetworzyć dokumentu PDF. Spróbuj ponownie.";
            logger.error('PDF direct OCR failed', { error: err instanceof Error ? err : errorMsg });
            setErrorMessage(errorMsg);
            /*
             * Navigate first, then cancel - and cancel with cancelProcessing rather
             * than handleCancelProcessing, which ends in goBack().
             *
             * That goBack() was the bug (DYS-037): handleCancelProcessing was called
             * without await, so it landed *after* navigateTo('error') and popped the
             * error screen straight off again, dropping the user back on the PDF view
             * with the job apparently forgotten and no message anywhere. It is most of
             * why this class of failure produced so few bug reports - the user never
             * saw the error, so never reached the copy-logs button on it.
             *
             * The order matters for a second reason. cancelProcessing clears the
             * navigation storage, which resets the stack's isJumping flag, and that
             * flag is what makes navigateTo replace the temporary processing screen
             * instead of stacking on top of it. Cancelling first leaves a dead
             * processing screen underneath the error for Back to land on.
             */
            navManager.navigateTo('error');
            await cancelProcessing();
        }
    }

    const handleOpenFile = async (file: File) => {
        logger.info('File opened', { fileName: file.name, fileSize: file.size, fileType: file.type });
        
        // Yield to UI to prevent freeze
        await new Promise(resolve => setTimeout(resolve, 0));
        
        const isPdf = file.type.includes("pdf");
        const isDocx =
            file.name.toLowerCase().endsWith('.docx') ||
            file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

        if (isDocx) {
            // For Word documents opened from the preview flows, skip image/PDF preview
            // and go directly to the DOCX import flow.
            await handleFileUpload(file, 0, true);
            return;
        }

        clearImageCollection(); // Clear any previous collection
        addImageToCollection(file);
        
        if (isPdf) {
            // Small delay to ensure UI updates before heavy PDF processing starts
            await new Promise(resolve => setTimeout(resolve, 50));
            navManager.navigateTo('pdfView')
            return
        } else {
            navManager.navigateTo("openFilePicture")
        }
    }

    const ocrFile = async (
        file: File,
        rotation: number,
        allowEmpty: boolean = false,
        context?: { docId: string; pageIndex: number; docTypeHint?: 'book' | 'form' | 'generic' }
    ): Promise<PreparedOcrPageResult> => {
        void rotation;
        const pageIndex = context?.pageIndex ?? 0;

        try {
            const result: PerformOcrResult = await performOCR(file, {
                docId: context?.docId,
                pageIndex,
                retryBudget: OCR_RETRY_LADDER_ENABLED ? 3 : 1,
                signal: currentSignal(),
            });

            logger.info('OCR file processed', {
                pageIndex,
                provider: result.provider,
                textLength: result.text.length,
                status: result.status,
                usedFallback: result.usedFallback,
                attempts: result.attempts.length,
            });

            result.attempts.forEach((attempt) => {
                logger.debug('OCR attempt details', {
                    pageIndex,
                    attempt: attempt.attempt,
                    provider: attempt.provider,
                    status: attempt.status,
                    promptProfile: attempt.promptProfile,
                    preprocessProfile: attempt.preprocessProfile,
                    textLength: attempt.textLength,
                    durationMs: attempt.durationMs,
                });
            });

            const hasText = result.text.trim().length > 0;
            // Clean up obvious gutter/edge fragments before normalization
            const cleanedRawText = cleanupOcrText(result.text, {
                docTypeHint: context?.docTypeHint || 'generic',
            });
            const detectedDocType = detectDocTypeFromText(cleanedRawText);
            const effectiveDocType: 'book' | 'form' | 'generic' =
                context?.docTypeHint === 'form' || detectedDocType === 'form'
                    ? 'form'
                    : context?.docTypeHint === 'book' || detectedDocType === 'book'
                        ? 'book'
                        : 'generic';

            logger.debug('OCR doc type resolved for normalization', {
                pageIndex,
                incomingDocTypeHint: context?.docTypeHint || 'generic',
                detectedDocType,
                effectiveDocType,
            });

            let status: OcrPageResult['status'];
            if (hasText) {
                status = result.provider === 'tesseract' && result.usedFallback
                    ? 'fallback_tesseract_ok'
                    : 'gemini_ok';
            } else if (result.attempts.some((attempt) => attempt.provider === 'gemini' && attempt.status === 'blocked')) {
                status = 'gemini_blocked';
            } else if (result.attempts.some((attempt) => attempt.provider === 'gemini' && attempt.status === 'empty')) {
                status = 'gemini_empty';
            } else {
                status = 'failed';
            }

            if (!hasText && !allowEmpty) {
                if (status === 'gemini_blocked') {
                    throw new Error("recognition: Treść strony została zablokowana przez filtr bezpieczeństwa. Może zawierać dane osobowe.");
                }
                throw new Error("recognition: Nie udało się rozpoznać tekstu w obrazie. Spróbuj użyć wyraźniejszego zdjęcia z lepszym oświetleniem.");
            }

            return {
                pageIndex,
                status,
                text: cleanedRawText,
                rawText: result.rawText,
                normalized: false,
                provider: result.provider,
                usedFallback: result.usedFallback,
                blocked: result.blocked,
                blockReason: result.blockReason,
                attempts: result.attempts,
                effectiveDocType,
            };
        } catch (error) {
            // Must come before the allowEmpty branch. On the multi-page path that
            // branch turns any error into a 'failed' page, which the caller records
            // with a missing-page marker and persists - resurrecting a job the user
            // just cancelled on the next visit.
            if (isOcrCancellation(error)) {
                throw error;
            }

            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            logger.error('OCR file failed', { pageIndex, error: errorMsg });

            if (allowEmpty) {
                return {
                    pageIndex,
                    status: 'failed',
                    text: '',
                    rawText: '',
                    normalized: false,
                    provider: 'gemini',
                    usedFallback: false,
                    blocked: false,
                    attempts: [],
                    effectiveDocType: context?.docTypeHint ?? 'generic',
                };
            }

            if (error instanceof Error && error.message.startsWith("recognition:")) {
                throw error;
            }

            if (isOcrTimeout(error)) {
                throw new Error("recognition: " + TIMEOUT_MESSAGE);
            }

            if (error instanceof Error) {
                if (error.message.includes("network") || error.message.includes("fetch")) {
                    throw new Error("Błąd połączenia z serwerem. Sprawdź połączenie internetowe i spróbuj ponownie.");
                }

                if (error.message.includes("timeout")) {
                    throw new Error("Przetwarzanie trwało zbyt długo. Spróbuj z mniejszym obrazem lub lepszą jakością.");
                }
            }

            throw new Error("recognition: Nie udało się przetworzyć obrazu. Spróbuj ponownie lub użyj innego zdjęcia.");
        }
    }

    const handlePdfOcrToText = async (pages: File[]) => {
        if(isProcessing.current) {
            logger.debug('Processing already in progress, skipping');
            return;
        }
        isProcessing.current = true;
        beginJob();
        setOcrSummary(null);
        
        const totalSize = pages.reduce((sum, p) => sum + p.size, 0);
        logger.info('Multi-page OCR started', { pageCount: pages.length, totalSize });

        setJobContext({
            source: getJobContext().source ?? 'pdf-pages',
            stage: 'ocr',
            fileCount: pages.length,
            totalBytes: totalSize,
            fileType: pages[0]?.type,
        });

        const noOfPagesInPdf = pages.length;
        
        if(!isProcessing.current) return;
        
        // Check if we're resuming processing
        const currentState = progressManager.getState();
        const isResuming = progressManager.isActive() && currentState.currentStep !== undefined;
        
        if (!isResuming) {
            // Initialize progress manager with the extracted pages (new processing)
            await progressManager.initialize(noOfPagesInPdf + 1, pages);
            logger.debug('New processing session initialized');
        } else {
            logger.info('Resuming processing', { fromStep: currentState.currentStep, totalSteps: currentState.totalSteps });
        }
        
        if(!isProcessing.current) return;
        navManager.jumpTo('processing')
        
        // Brief delay to ensure UI is ready before processing starts
        const delay = isResuming ? 200 : 500;
        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Check again after the timeout - processing might have been cancelled
        if(!isProcessing.current) return;

        const processingTimer = logger.startTimer('Multi-page OCR');
        const docId = createDocId();

        try {
            let allPagesText = "";
            const pageResults: OcrPageResult[] = [];
            let usedTesseract = false;
            let missingPageCount = 0;
            let blockedPageCount = 0;
            let successfulPageCount = 0;
            let offlineAtPageIndex: number | null = null;
            const currentState = progressManager.getState();
            const resumeAtFinalStep = isResuming && (currentState.currentStep ?? 0) > pages.length && !!currentState.processedText;
            // currentStep is 1-based count of completed pages (set via setStep(i + 1) after page i).
            const startFromStep = isResuming && currentState.currentStep !== undefined
                ? Math.max(0, Math.min(currentState.currentStep, pages.length))
                : 0;
            
            // If resuming, start with previously processed text
            if (isResuming && currentState.processedText) {
                allPagesText = currentState.processedText;
                logger.debug('Restored processed text', { textLength: allPagesText.length });
            }
            
            // Ensure we always start from 0 for new processing sessions
            const safeStartStep = Math.max(0, Math.min(startFromStep, pages.length));
            if (safeStartStep !== startFromStep) {
                logger.warn('Adjusted start step', { from: startFromStep, to: safeStartStep });
            }
            
            // Detect document type from the first successfully processed page text.
            /*
             * Every page votes, rather than the first page that happened to have
             * text deciding for all of them. P2.5 - see pickDocumentType.
             *
             * The running total also feeds the next page's OCR hint, so a document
             * that reveals itself on page 2 stops being read as generic from page 3
             * onwards instead of never.
             */
            const perPageDocTypes: ('book' | 'form' | 'generic')[] = [];
            let detectedDocType: 'book' | 'form' | 'generic' = 'generic';

            /*
             * Normalization runs per page, started but not awaited. P2.4.
             *
             * It used to be one whole-document call after the last page had been
             * OCR'd - serial, blocking, and the only thing left between the user and
             * their text. On a 72 000-character book that was 42 seconds of spinner
             * after the OCR had already finished.
             *
             * Awaiting each page inside the loop would move the call without moving
             * the wait: page N+1's OCR would not start until page N's tidy-up came
             * back, and the total would be unchanged. So each page's normalization is
             * kicked off and its promise collected, which lets it run against the
             * next page's OCR. The document now waits for the last page's
             * normalization rather than for the whole document's.
             *
             * Three things fall out of it, none of them the point but all worth
             * having:
             *
             *  - The page-break token count cannot drift any more. The tokens are
             *    inserted here, between texts that have already been normalized, so
             *    `page_breaks_mismatch` becomes a state the code cannot reach rather
             *    than a check that has to catch it.
             *  - A page whose normalization is rejected falls back to its own raw
             *    text instead of costing the whole document its line joining.
             *  - MAX_RAW_TEXT_LENGTH is measured per page, so a long book stops being
             *    skipped for being long.
             *
             * The cost the plan warned about - losing the join of a sentence broken
             * across a page boundary - was measured on 2026-08-25 and is worth less
             * than it sounds. The reader draws a block separator at every boundary
             * regardless, and until yesterday the joined form actively broke speech by
             * leaving the token mid-sentence. See P2.4 in docs/ocr-pipeline-plan.md.
             */
            interface NormalizedPage {
                text: string;
                normalized: boolean;
                usedRawFallback: boolean;
                rejectReason: string | null;
            }
            const pendingNormalizations: Promise<NormalizedPage>[] = [];

            const normalizePageInBackground = (pageIndex: number, pageText: string, docType: 'book' | 'form' | 'generic'): Promise<NormalizedPage> => {
                const raw: NormalizedPage = { text: pageText, normalized: false, usedRawFallback: true, rejectReason: null };
                if (!OCR_NORMALIZE_ENABLED || pageText.trim().length === 0) {
                    return Promise.resolve({ ...raw, usedRawFallback: false });
                }

                return normalizeOCRText(pageText, {
                    pageIndex,
                    docTypeHint: docType,
                    signal: currentSignal(),
                })
                    .then((normalizeResult) => {
                        const applied = applyNormalizationResult(pageText, normalizeResult, docType, pageIndex, 'multi-page');
                        logger.info('OCR page normalization completed', {
                            pageIndex,
                            usedRawFallback: normalizeResult.usedRawFallback,
                            rejectReason: normalizeResult.rejectReason,
                            driftRatio: normalizeResult.stats.driftRatio,
                            inputLength: normalizeResult.stats.inputLength,
                            outputLength: normalizeResult.stats.outputLength,
                        });
                        return {
                            text: applied.finalText,
                            normalized: applied.normalized,
                            usedRawFallback: applied.usedRawFallback,
                            rejectReason: normalizeResult.rejectReason ?? null,
                        };
                    })
                    /*
                     * Caught here rather than at the await, and deliberately.
                     * These promises are in flight while the loop keeps running, so
                     * an unhandled rejection - a cancelled job is the ordinary case -
                     * would surface as a crash before anything gets a chance to
                     * inspect it. A page that cannot be tidied keeps its own text.
                     */
                    .catch((error) => {
                        if (!isOcrCancellation(error)) {
                            logger.warn('OCR page normalization failed, keeping raw text', {
                                pageIndex,
                                error: error instanceof Error ? error.message : String(error),
                            });
                        }
                        return raw;
                    });
            };

            for (let i = safeStartStep; i < pages.length; i++) {
                if(!isProcessing.current) return;
                const page = pages[i];

                logger.debug('Processing page', { page: i + 1, total: pages.length, fileName: page.name });
                setJobContext({ pageIndex: i });
                
                try {
                    const result = await ocrFile(page, 0, true, { docId, pageIndex: i, docTypeHint: detectedDocType });
                    pageResults.push(result);

                    if (result.status === 'fallback_tesseract_ok') {
                        usedTesseract = true;
                    }

                    if (result.status === 'gemini_blocked') {
                        blockedPageCount++;
                        logger.warn('Page blocked by safety filter', { page: i + 1 });
                    }

                    const hasMissingContent = result.status === 'gemini_empty' || result.status === 'gemini_blocked' || result.status === 'failed';
                    if (hasMissingContent) {
                        missingPageCount++;
                        logger.warn('Page missing text after OCR pipeline', {
                            page: i + 1,
                            status: result.status,
                        });
                    } else {
                        successfulPageCount++;
                    }

                    const pageOutputText = result.text.trim().length > 0 ? result.text : createMissingPageMarker(i + 1);

                    if (result.text.trim().length > 0) {
                        const pageDocType = detectDocTypeFromText(result.text);
                        perPageDocTypes.push(pageDocType);

                        const previous: 'book' | 'form' | 'generic' = detectedDocType;
                        detectedDocType = pickDocumentType(perPageDocTypes);

                        if (detectedDocType !== previous) {
                            logger.info('Document type changed on the evidence of another page', {
                                docTypeHint: detectedDocType,
                                previous,
                                pageDocType,
                                page: i + 1,
                                pagesVoted: perPageDocTypes.length,
                            });
                        }
                    }
                    if (allPagesText === "") {
                        allPagesText = pageOutputText;
                    } else {
                        allPagesText += `\n${PAGE_BREAK_TOKEN}\n${pageOutputText}`;
                    }

                    // Started, not awaited: this runs against the next page's OCR.
                    pendingNormalizations.push(normalizePageInBackground(i, pageOutputText, detectedDocType));
                } catch (pageError) {
                    if (isOcrCancellation(pageError)) {
                        throw pageError;
                    }

                    /*
                     * Being offline is the one failure where the next page cannot
                     * possibly go better, and asking anyway is not free: a report
                     * from production showed pages 17 to 62 of one document failing
                     * this way in 16 seconds, each one written off as a missing page,
                     * leaving the user a document three quarters empty with nothing
                     * saying why. Stop here instead. The break skips the setStep for
                     * this page, so the persisted progress still describes the last
                     * page that actually succeeded and a retry resumes from there.
                     */
                    if (isOcrOffline(pageError)) {
                        offlineAtPageIndex = i;
                        break;
                    }

                    const errorMsg = pageError instanceof Error ? pageError.message : 'Unknown error';
                    logger.warn('Page OCR failed, writing missing-page marker', { page: i + 1, error: errorMsg });
                    missingPageCount++;

                    pageResults.push({
                        pageIndex: i,
                        status: 'failed',
                        text: '',
                        rawText: '',
                        normalized: false,
                        provider: 'gemini',
                        usedFallback: false,
                        blocked: false,
                        attempts: [],
                        effectiveDocType: detectedDocType,
                    });

                    const markerText = createMissingPageMarker(i + 1);
                    if (allPagesText === "") {
                        allPagesText = markerText;
                    } else {
                        allPagesText += `\n${PAGE_BREAK_TOKEN}\n${markerText}`;
                    }
                }
                
                // Save progress with accumulated text
                await progressManager.setStep(i + 1, allPagesText);
                
                if(!isProcessing.current) return;
            }
            
            if(!isProcessing.current) return;

            if (offlineAtPageIndex !== null) {
                /*
                 * Deliberately does not call handleCancelProcessing: that resets the
                 * progress manager, which is exactly the record of the pages already
                 * recognised. Leaving it in place is what lets the retry resume.
                 */
                logger.warn('Multi-page OCR stopped because the device is offline', {
                    completedPages: offlineAtPageIndex,
                    totalPages: pages.length,
                    stoppedAtPage: offlineAtPageIndex + 1,
                });
                isProcessing.current = false;
                setErrorMessage(offlineMessage(offlineAtPageIndex, pages.length));
                navManager.navigateTo('error');
                return;
            }
            
            // Log final statistics
            logger.info('Multi-page OCR completed', { 
                totalPages: pages.length,
                successfulPages: successfulPageCount,
                missingPages: missingPageCount,
                blockedPages: blockedPageCount,
            });
            
            if (allPagesText.trim().length === 0) {
                throw new Error("recognition: Nie udało się zbudować treści dokumentu po OCR. Spróbuj ponownie.");
            }

            await progressManager.setStep(noOfPagesInPdf, allPagesText);

            if(!isProcessing.current) return;

            let finalDocumentText = allPagesText;
            let documentNormalized = false;
            let documentUsedRawFallback = false;
            let documentRejectReason: string | null = null;
            const rawPageBreakCount = countTokenOccurrences(allPagesText, PAGE_BREAK_TOKEN);
            let normalizedPageBreakCount: number | null = null;
            const expectedNormalizeCalls = allPagesText.trim().length > 0 && OCR_NORMALIZE_ENABLED && !resumeAtFinalStep
                ? pendingNormalizations.length
                : 0;
            let actualNormalizeCalls = 0;
            let normalizedPageFlags: boolean[] = [];

            if (resumeAtFinalStep) {
                logger.info('Resume detected at final normalization step, reusing restored text');
                finalDocumentText = currentState.processedText || allPagesText;
            } else if (OCR_NORMALIZE_ENABLED) {
                setJobContext({ stage: 'normalize' });

                /*
                 * By the time the loop ends, every page but the last has usually
                 * finished already - it was tidying itself while the next page was
                 * being read. This waits for whatever is left, which on a long
                 * document is one page rather than the whole thing.
                 */
                const normalizedPages = await Promise.all(pendingNormalizations);
                actualNormalizeCalls = normalizedPages.length;
                normalizedPageFlags = normalizedPages.map((page) => page.normalized);

                /*
                 * A resumed job only re-reads the pages it had not reached, so this
                 * list covers those and nothing earlier. The text from before the
                 * interruption was restored into allPagesText and has to be put back
                 * in front, or resuming a document silently returns only its tail.
                 *
                 * Kept out of the list rather than pushed onto it, so the flags stay
                 * index-aligned with pageResults - which also only holds this run's
                 * pages.
                 */
                const restoredPrefix = isResuming && currentState.processedText ? currentState.processedText : '';

                /*
                 * The tokens go in here, between texts that have already been
                 * normalized, so the count is right by construction. The old
                 * `page_breaks_mismatch` check existed because a whole-document pass
                 * could quietly drop one; that is no longer a state this code can
                 * reach.
                 */
                finalDocumentText = [restoredPrefix, ...normalizedPages.map((page) => page.text)]
                    .filter((part) => part.trim().length > 0)
                    .join(`\n${PAGE_BREAK_TOKEN}\n`);
                normalizedPageBreakCount = countTokenOccurrences(finalDocumentText, PAGE_BREAK_TOKEN);

                documentNormalized = normalizedPages.some((page) => page.normalized);
                documentUsedRawFallback = normalizedPages.every((page) => page.usedRawFallback);
                documentRejectReason = normalizedPages.find((page) => page.rejectReason)?.rejectReason ?? null;

                logger.info('OCR document normalization completed', {
                    pages: normalizedPages.length,
                    normalizedPages: normalizedPages.filter((page) => page.normalized).length,
                    rawFallbackPages: normalizedPages.filter((page) => page.usedRawFallback).length,
                    rejectReasons: normalizedPages.map((page) => page.rejectReason).filter(Boolean),
                    restoredPrefixLength: restoredPrefix.length,
                    inputLength: allPagesText.length,
                    outputLength: finalDocumentText.length,
                });
            } else {
                logger.debug('Document normalization skipped by feature flag', {
                    pageCount: pages.length,
                });
            }

            await progressManager.setStep(noOfPagesInPdf + 1, finalDocumentText);

            if(!isProcessing.current) return;

            logger.info('Document normalization verification', {
                docId,
                pageCount: pages.length,
                expectedNormalizeCalls,
                actualNormalizeCalls,
                normalizeCallCountMatch: expectedNormalizeCalls === actualNormalizeCalls,
                rawPageBreakCount,
                normalizedPageBreakCount,
                pageBreakTokenPreserved: normalizedPageBreakCount === null ? null : rawPageBreakCount === normalizedPageBreakCount,
                documentNormalized,
                documentUsedRawFallback,
                documentRejectReason,
            });

            /*
             * Per page, now that it can be. This used to read `documentNormalized`,
             * because one whole-document pass either worked for everything or for
             * nothing - so a page that was tidied perfectly was reported as raw
             * whenever some other page dragged the document into a fallback.
             */
            pageResults.forEach((pageResult, index) => {
                const pageHasText = pageResult.text.trim().length > 0;
                const pageIsMissing = pageResult.status === 'gemini_empty' || pageResult.status === 'gemini_blocked' || pageResult.status === 'failed';
                const pageWasNormalized = normalizedPageFlags[index] ?? documentNormalized;
                pageResult.normalized = pageHasText && !pageIsMissing && pageWasNormalized;
            });

            setProcessedText(finalDocumentText);
            const finalProvider = usedTesseract ? 'tesseract' : 'gemini';
            setJobContext({ stage: 'display', provider: finalProvider });
            setOcrProvider(finalProvider);
            const summary = buildOcrSummary(pageResults);
            if (summary.totalPages < pages.length) {
                // For resumed sessions, unknown historical pages are treated as gemini pages.
                summary.totalPages = pages.length;
                summary.geminiPages = Math.max(0, pages.length - summary.tesseractPages - summary.missingPages);
            }
            setOcrSummary(summary);
            
            processingTimer({ 
                pageCount: pages.length, 
                provider: finalProvider, 
                textLength: finalDocumentText.length,
                successfulPages: successfulPageCount,
                missingPages: missingPageCount,
                blockedPages: blockedPageCount,
                tesseractPages: summary.tesseractPages,
                fallbackPageIndices: summary.fallbackPageIndices.join(','),
                missingPageIndices: summary.missingPageIndices.join(','),
                expectedNormalizeCalls,
                actualNormalizeCalls,
                normalizeCallCountMatch: expectedNormalizeCalls === actualNormalizeCalls,
                rawPageBreakCount,
                normalizedPageBreakCount: normalizedPageBreakCount ?? '',
                documentNormalized,
                documentUsedRawFallback,
                documentRejectReason,
            });
            
            if(!isProcessing.current) return;
            
            // Wait for completion animation (500ms) + pause at 100% for user to see (300ms)
            await new Promise(resolve => setTimeout(resolve, 800));
            
            if(!isProcessing.current) return;
            await progressManager.reset(); // Clear processing state when complete
            navManager.jumpTo('display')
            isProcessing.current = false;
        } catch (err) {
            if (isOcrCancellation(err)) {
                logger.info('Multi-page OCR aborted by cancellation');
                return;
            }
            if(!isProcessing.current) return;
            isProcessing.current = false;
            const errorMsg = isOcrTimeout(err)
                ? TIMEOUT_MESSAGE
                : err instanceof Error ? err.message : "Nie udało się przetworzyć dokumentu PDF. Spróbuj ponownie.";
            logger.error('Multi-page OCR failed', { error: err instanceof Error ? err : errorMsg, pagesProcessed: progressManager.getState().currentStep });
            setErrorMessage(errorMsg);
            /*
             * Navigate first, then cancel - and cancel with cancelProcessing rather
             * than handleCancelProcessing, which ends in goBack().
             *
             * That goBack() was the bug (DYS-037): handleCancelProcessing was called
             * without await, so it landed *after* navigateTo('error') and popped the
             * error screen straight off again, dropping the user back on the PDF view
             * with the job apparently forgotten and no message anywhere. It is most of
             * why this class of failure produced so few bug reports - the user never
             * saw the error, so never reached the copy-logs button on it.
             *
             * The order matters for a second reason. cancelProcessing clears the
             * navigation storage, which resets the stack's isJumping flag, and that
             * flag is what makes navigateTo replace the temporary processing screen
             * instead of stacking on top of it. Cancelling first leaves a dead
             * processing screen underneath the error for Back to land on.
             */
            navManager.navigateTo('error');
            await cancelProcessing();
        }
    }

    const cancelProcessing = async () => {
        logger.info('Processing cancelled');
        isProcessing.current = false;
        // Abort before resetting state: the in-flight request has to stop, otherwise
        // its continuation can re-persist progress for a job the user just cancelled.
        jobAbortRef.current?.abort();
        jobAbortRef.current = null;
        clearJobContext();
        // Clear progress state and storage immediately
        await progressManager.reset();
        // Clear navigation storage to prevent restoring cancelled state
        navManager.clearStorage();
    }

    const handleCancelProcessing = async () => {
        await cancelProcessing();
        // Navigate back
        navManager.goBack();
    }


    return (
        <>
            {currentScreen === "home" && (
                <HomeScreen onFileUpload={handleOpenFile} onCameraCapture={handleCamera}
                    onError={handleError} onNavigateToHome={handleNavigateToHome} />
            )}

            {currentScreen === "processing" && (
                <ProcessingScreen
                    externalStep={progressState.currentStep}
                    externalStepsNumber={progressState.totalSteps}
                    onCancel={handleCancelProcessing}
                    onNavigateToHome={handleNavigateToHome}
                />
            )}

            {currentScreen === "display" && (
                <TextDisplayScreen
                    text={processedText}
                    ocrProvider={ocrProvider}
                    ocrSummary={ocrSummary}
                    onBackToHome={handleBackToHome}
                    onNavigateToHome={handleNavigateToHome}
                />
            )}

            {currentScreen === "error" && <ErrorScreen message={errorMessage} onBackToHome={handleBackToHome} onNavigateToHome={handleNavigateToHome} />}

            {currentScreen === "camera" &&
                <CameraScreen onBackToHome={handleBackToHome}
                    onCameraCapture={handleCameraPicture} onNavigateToHome={handleNavigateToHome} />}

            {currentScreen === "cameraPicture" && cameraImageSrc &&
                <PictureScreen 
                    onBackToHome={handleBackToHome}
                    pictureFile={cameraImageSrc} 
                    onOCRPicture={handleCameraPictureOCR}
                    onOCRCollection={handleOCRImageCollection}
                    onNavigateToHome={handleNavigateToHome}
                    source="camera"
                    onRetake={handleRetakeFromCamera}
                    imageCollection={imageCollection}
                    currentImageIndex={currentImageIndex}
                    onAddMore={handleAddMoreFromCamera}
                    onNavigateToImage={navigateToImageInCollection}
                    onRemoveFromCollection={removeImageFromCollection}
                />}

            {currentScreen === "openFilePicture" && cameraImageSrc &&
                <PictureScreen 
                    onBackToHome={handleBackToHome}
                    pictureFile={cameraImageSrc} 
                    onOCRPicture={handleOpenedPictureOCR}
                    onOCRCollection={handleOCRImageCollection}
                    onNavigateToHome={handleNavigateToHome}
                    source="file"
                    onChangeFile={(file) => {
                        if (file) {
                            // If a new file is provided, update the current file
                            if (file.type.includes("pdf")) {
                                // If it's a PDF, navigate to PDF view
                                clearImageCollection();
                                addImageToCollection(file);
                                navManager.navigateTo('pdfView');
                            } else {
                                // For images, just update the state (stays on same screen)
                                updateCurrentImage(file);
                            }
                        } else {
                            // Fallback to old behavior if no file provided
                            navManager.goBack();
                        }
                    }}
                    onError={handleError}
                    imageCollection={imageCollection}
                    currentImageIndex={currentImageIndex}
                    onAddMore={handleAddMoreToCollection}
                    onNavigateToImage={navigateToImageInCollection}
                    onRemoveFromCollection={removeImageFromCollection}
                />}
            {currentScreen === "pdfView" && imageCollection.length > 0 &&
                <PdfViewScreen 
                    onBackToHome={handleBackToHome}
                    pdfFile={imageCollection[0]} 
                    onPdfOcrToText={handlePdfOcrToText}
                    onPdfDirectToText={PDF_SEND_DIRECT_TO_GEMINI ? handlePdfDirectToText : undefined}
                    onNavigateToHome={handleNavigateToHome}
                    onChangeFile={(file) => {
                        clearImageCollection();
                        addImageToCollection(file);
                        if (file.type.includes("image")) {
                            // If switching to an image, navigate to picture view
                            navManager.navigateTo('openFilePicture');
                        }
                        // If PDF, stay on pdfView - component re-renders with new file
                    }}
                    onError={handleError}
                />}
        </>
    )
}
